/**
 * CR098: Single source of truth for deriving deal type badge
 * 
 * This helper resolves the canonical deal type from multiple possible fields,
 * providing consistent behavior across all deal card components.
 * 
 * Priority order:
 * 1. deal.deal_category (uppercase enum)
 * 2. deal.deal_type (lowercase string, normalized to uppercase)
 * 3. canonical_deal_json.deal_type (if present)
 * 4. canonical_deal_json.type (legacy fallback)
 * 5. Infer from deal properties (points, in_kind_tiers, discount fields)
 * 6. Default to UNCLASSIFIED
 */

import type { Deal, DealCategory } from "@/lib/types/deals";

// Valid deal categories
const VALID_CATEGORIES: DealCategory[] = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

// Normalize string to DealCategory
function normalizeToCategory(value: string | null | undefined): DealCategory | null {
  if (!value) return null;
  const upper = value.toUpperCase().replace(/-/g, "_");
  if (VALID_CATEGORIES.includes(upper as DealCategory)) {
    return upper as DealCategory;
  }
  // Handle common variations
  if (upper === "IN-KIND" || upper === "INKIND") return "IN_KIND";
  // CR335: Fold all bonus flavors into canonical BONUS
  if (upper === "GIFT" || upper === "BONUS_CARD" || upper === "BONUS_SAME" || upper === "BONUS_DIFFERENT" || upper === "BONUS_SAME_BACK") return "BONUS";
  // CR335: PERCENT_BACK is DISCOUNT, not BONUS
  if (upper === "PERCENT_BACK") return "DISCOUNT";
  if (upper === "LOYALTY_POINTS") return "POINTS";
  return null;
}

export interface DealTypeDebugInfo {
  source: "deal_category" | "deal_type" | "canonical_deal_type" | "canonical_type" | "inferred" | "default";
  rawValue: string | null;
  fieldsPresent: {
    deal_category: boolean;
    deal_type: boolean;
    canonical_deal_json: boolean;
    points_amount: boolean;
    in_kind_tiers: boolean;
    discount_fields: boolean;
  };
}

export interface GetDealTypeResult {
  category: DealCategory;
  debug: DealTypeDebugInfo;
}

/**
 * Get the canonical deal type/category for any deal object.
 * 
 * @param deal - The deal object (can be Deal, PreviewDeal, or any deal-like object)
 * @returns The resolved DealCategory and debug info
 */
export function getDealType(deal: Partial<Deal> & { canonical_deal_json?: Record<string, unknown> }): GetDealTypeResult {
  const debugInfo: DealTypeDebugInfo = {
    source: "default",
    rawValue: null,
    fieldsPresent: {
      deal_category: Boolean(deal.deal_category),
      deal_type: Boolean(deal.deal_type),
      canonical_deal_json: Boolean((deal as any).canonical_deal_json),
      points_amount: Boolean(deal.points_amount && deal.points_amount > 0),
      in_kind_tiers: Boolean(deal.in_kind_tiers && deal.in_kind_tiers.length > 0),
      discount_fields: Boolean(
        deal.discount_subtype || 
        deal.discount_amount_cad || 
        (deal.pay_amount_cad && deal.receive_value_cad)
      ),
    },
  };

  // 1. Check deal.deal_category first (uppercase enum)
  if (deal.deal_category) {
    const normalized = normalizeToCategory(deal.deal_category);
    if (normalized && normalized !== "UNCLASSIFIED") {
      debugInfo.source = "deal_category";
      debugInfo.rawValue = deal.deal_category;
      return { category: normalized, debug: debugInfo };
    }
  }

  // 2. Check deal.deal_type (lowercase string)
  if (deal.deal_type) {
    const normalized = normalizeToCategory(deal.deal_type);
    if (normalized && normalized !== "UNCLASSIFIED") {
      debugInfo.source = "deal_type";
      debugInfo.rawValue = deal.deal_type;
      return { category: normalized, debug: debugInfo };
    }
  }

  // 3. Check canonical_deal_json.deal_type
  const canonical = (deal as any).canonical_deal_json;
  if (canonical?.deal_type) {
    const normalized = normalizeToCategory(canonical.deal_type);
    if (normalized && normalized !== "UNCLASSIFIED") {
      debugInfo.source = "canonical_deal_type";
      debugInfo.rawValue = canonical.deal_type;
      return { category: normalized, debug: debugInfo };
    }
  }

  // 4. Check canonical_deal_json.type (legacy fallback)
  if (canonical?.type) {
    const normalized = normalizeToCategory(canonical.type);
    if (normalized && normalized !== "UNCLASSIFIED") {
      debugInfo.source = "canonical_type";
      debugInfo.rawValue = canonical.type;
      return { category: normalized, debug: debugInfo };
    }
  }

  // 5. Infer from deal properties
  debugInfo.source = "inferred";

  // Check for In-Kind deals
  if (deal.in_kind_tiers && deal.in_kind_tiers.length > 0) {
    debugInfo.rawValue = "inferred:in_kind_tiers";
    return { category: "IN_KIND", debug: debugInfo };
  }

  // Check for Points deals
  if (deal.points_amount && deal.points_amount > 0) {
    debugInfo.rawValue = "inferred:points_amount";
    return { category: "POINTS", debug: debugInfo };
  }

  // Check for Discount deals
  if (
    deal.discount_subtype || 
    deal.discount_amount_cad ||
    (deal.pay_amount_cad && deal.receive_value_cad)
  ) {
    debugInfo.rawValue = "inferred:discount_fields";
    return { category: "DISCOUNT", debug: debugInfo };
  }

  // Check for Bonus indicators (spend/bonus amounts)
  if (
    (deal.spend_amount && deal.bonus_amount) ||
    (deal.qualifying_spend_cad && deal.bonus_value_cad) ||
    deal.bonus_merchant
  ) {
    debugInfo.rawValue = "inferred:bonus_fields";
    return { category: "BONUS", debug: debugInfo };
  }

  // 6. Default - truly unclassified
  debugInfo.source = "default";
  debugInfo.rawValue = null;
  return { category: "UNCLASSIFIED", debug: debugInfo };
}

/**
 * Simple wrapper that just returns the category without debug info.
 * Use this when you don't need diagnostic information.
 */
export function getDealCategory(deal: Partial<Deal>): DealCategory {
  return getDealType(deal).category;
}

/**
 * Log debug info for UNCLASSIFIED deals (admin mode only).
 * Call this from DealGiftCard when isAdmin=true and category is UNCLASSIFIED.
 */
export function logUnclassifiedDeal(
  dealId: string | undefined,
  dedupeKey: string | undefined,
  debugInfo: DealTypeDebugInfo
): void {
  console.warn(`[UNCLASSIFIED DEAL] id=${dealId || "??"}, dedupe_key=${dedupeKey || "??"}`, {
    source: debugInfo.source,
    rawValue: debugInfo.rawValue,
    fieldsPresent: debugInfo.fieldsPresent,
  });
}
